import Image from "next/image";
import Link from "next/link";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";
import { AudienceSection } from "@/components/blocks/AudienceSection";

export function HeroBlock({
  content,
  template = "classic",
  headingLevel = 1,
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
  headingLevel?: 1 | 2;
}) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const subheading = String(resolved.subheading ?? "");
        const buttonText = resolved.buttonText ? String(resolved.buttonText) : null;
        const buttonUrl = resolved.buttonUrl ? String(resolved.buttonUrl) : null;
        const backgroundImage = resolved.backgroundImage ? String(resolved.backgroundImage) : null;
        const logoUrl = resolved.logoUrl ? String(resolved.logoUrl) : null;
        const logoAlt = resolved.logoAlt ? String(resolved.logoAlt) : "";
        const overlay = Boolean(resolved.overlay);
        const objectPosition = resolved.objectPosition ? String(resolved.objectPosition) : "center center";
        const styles = getTemplateStyles(template);
        const HeadingTag = headingLevel === 1 ? "h1" : "h2";

        return (
          <section data-template={template} className={styles.hero.section}>
            {backgroundImage && (
              <Image
                src={backgroundImage}
                alt=""
                fill
                className="object-cover"
                style={{ objectPosition }}
                priority
                sizes="100vw"
              />
            )}
            {overlay && styles.hero.overlay && <div className={styles.hero.overlay} />}
            <div className={styles.hero.container}>
              <div className="flex flex-col gap-[calc(var(--space-unit)*1)]">
                {logoUrl && (
                  <div className="mb-2 inline-flex w-fit rounded-2xl bg-white/95 p-4 shadow-xl ring-1 ring-black/10 backdrop-blur-sm">
                    <Image
                      src={logoUrl}
                      alt={logoAlt}
                      width={148}
                      height={148}
                      className="h-28 w-28 object-contain md:h-36 md:w-36"
                      priority
                    />
                  </div>
                )}
                <HeadingTag
                  className={`whitespace-pre-line font-bold ${styles.hero.headingColor}`}
                  style={{
                    fontFamily: "var(--font-heading)",
                    fontSize: styles.hero.headingSize,
                    lineHeight: "var(--line-display)",
                  }}
                >
                  {heading}
                </HeadingTag>
                {subheading && (
                  <p className={`max-w-3xl text-lg ${styles.hero.subheadingColor}`}>{subheading}</p>
                )}
                {buttonText && buttonUrl && (
                  <Link href={buttonUrl} className={styles.hero.button}>
                    {buttonText}
                  </Link>
                )}
              </div>
            </div>
          </section>
        );
      }}
    </AudienceSection>
  );
}
